import { getTrackingParams } from './tracking'
import { isAnnualPlan } from './storage'
import { getEfTransactionId } from './everflow'
import { getSelectedCharacterSlug } from './postSignupRedirect'

const SWIPEY_BASE_URL = process.env.REACT_APP_SWIPEY_BASE_URL

// Backend reads only `path` and `model-params` from the Google OAuth state
// and ignores everything else. To get plans/tracking onto the post-OAuth
// paywall URL without a backend change, we encode them into `path` itself —
// backend builds the final URL via `new URL(BASE + path)`, which parses the
// embedded query string correctly.
export const buildOAuthRedirectPath = (postSignupRedirect, selections, theme) => {
  const params = new URLSearchParams()

  // Tell swipey.ai paywall this user came from the trans lander so the
  // post-payment redirect lands on /chats/{slug} instead of the homepage.
  // `selected_slug` is the param the xDate paywall reads to know which
  // chat to push the user into after they pay. Verified against
  // xDate/frontend/src/components/v2/organisms/paywall/quiz-landing-premium-page.tsx
  // (reads `selected_slug` from URL → router.replace(`/chats/${selectedSlug}`)).
  params.set('from_landing', 'true')
  params.set('selected_slug', getSelectedCharacterSlug())

  if (isAnnualPlan() || postSignupRedirect === 'annual') {
    params.set('plans', 'annual')
  }

  if (selections?.welcome) {
    params.set('type', selections.welcome === 'Anime' ? 'anime' : 'realistic')
  }

  if (theme) params.set('theme', theme)

  const efTransactionId = getEfTransactionId()
  if (efTransactionId) params.set('ef_transaction_id', efTransactionId)

  const trackingParams = getTrackingParams()
  Object.entries(trackingParams).forEach(([key, value]) => {
    if (value) params.set(key, value)
  })

  const queryString = params.toString()
  return queryString ? `/paywall?${queryString}` : '/paywall'
}

export const buildPaywallUrl = (
  authToken,
  authType = 'login_paywall',
  modelParams = null,
  style = 'Realistic',
  model,
  pw,
  halloween,
  theme,
  postSignupRedirect,
) => {
  const paywallUrl = new URL(SWIPEY_BASE_URL + '/paywall')

  paywallUrl.searchParams.set('auth_token', authToken)
  paywallUrl.searchParams.set('auth_type', authType)

  // Tell swipey.ai paywall this user came from the trans lander so the
  // post-payment redirect lands on /chats/{slug} instead of the homepage.
  // `selected_slug` is the param the xDate paywall reads to know which
  // chat to push the user into after they pay. Verified against
  // xDate/frontend/src/components/v2/organisms/paywall/quiz-landing-premium-page.tsx
  // (reads `selected_slug` from URL → router.replace(`/chats/${selectedSlug}`)).
  paywallUrl.searchParams.set('from_landing', 'true')
  paywallUrl.searchParams.set('selected_slug', getSelectedCharacterSlug())

  const typeValue = style === 'Anime' ? 'anime' : 'realistic'
  paywallUrl.searchParams.set('type', typeValue)

  const efTransactionId = getEfTransactionId()
  if (efTransactionId) {
    paywallUrl.searchParams.set('ef_transaction_id', efTransactionId)
  }

  const trackingParams = getTrackingParams()
  Object.entries(trackingParams).forEach(([key, value]) => {
    if (value) paywallUrl.searchParams.set(key, value)
  })

  // Intentionally NOT setting `model-params` on the paywall URL — xDate's
  // useHandleAttributesInQueryParams hook would cache it under
  // `createAiParamsAfterPaywall` and then `premiumOnClose` would route to
  // /generating-ai instead of /chats/{selected_slug}.
  // See xDate/frontend/src/hooks/usePaymentSuccessHandlers.ts (premiumOnClose).
  if (model) {
    paywallUrl.searchParams.set('media', model)
    paywallUrl.searchParams.set('pw_v', '1')
  }
  if (pw) {
    paywallUrl.searchParams.set('media', 'vid.mp4')
    paywallUrl.searchParams.set('pw_v', pw)
  }
  if (halloween) paywallUrl.searchParams.set('media', halloween)
  if (theme) paywallUrl.searchParams.set('theme', theme)

  // /12 route already forces annual; otherwise GB flag can opt traffic into it.
  // Why: experiment is scoped out on /12 by product decision.
  const annualByRoute = isAnnualPlan()
  const annualByFlag = !annualByRoute && postSignupRedirect === 'annual'
  if (annualByRoute || annualByFlag) paywallUrl.searchParams.set('plans', 'annual')

  return paywallUrl.toString()
}
